import Docxtemplater from 'docxtemplater';
import expressionParser from 'docxtemplater/expressions';
import { saveAs } from 'file-saver';
import { TFunction } from 'i18next';
import _ from 'lodash';
import moment from 'moment/moment';
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils/index.js';

import { LongAbsence, ShortAbsence } from '~/data/absences';
import { CostCenter } from '~/data/costCenters';
import {
  EmpPosition,
  Employee,
  EmployeeCostCenter,
  getEmployeeData,
} from '~/data/employee';
import { EmployeesIndexed } from '~/data/employees';
import {
  EmploymentDetailData,
  EmploymentWorkingHours,
  FullEmploymentData,
  Office,
} from '~/data/employment/types';
import { AllEnums, Enum } from '~/data/enums';
import { EmployeeOtherHistoryType } from '~/data/history/useEmployeeOtherHistory';
import { LegalEntity } from '~/data/legalEntity';
import { OrganizationUnit } from '~/data/orgunits';
import { PendingScheduleChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import {
  CustomFieldValues,
  FieldAttributes,
} from '~/legacy-components/CustomDataFields';
import { EmplHistoryType } from '~/legacy-components/ScheduleHistory/components/EmplHistory/data';

const loadFile = (
  url: string,
  callback: (error: Error, content: PizZip.LoadData) => void,
) => {
  PizZipUtils.getBinaryContent(url, callback);
};

export const generateDocument = <T>(
  url: string,
  placeholders: T,
  outputFileName: string,
) => {
  loadFile(url, (error, content) => {
    if (error) {
      throw error;
    }
    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      parser: expressionParser,
    });
    doc.render(placeholders);
    const out = doc.getZip().generate({
      type: 'blob',
      mimeType:
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    saveAs(out, outputFileName);
  });
};

export const getDocumentWorkContractPlaceholders = (
  selectedEmployment: PendingScheduleChanges,
  isCustomDataEnabled: boolean,
  jsonEnumALL: AllEnums,
  jsonAllEmp: EmployeesIndexed,
  jsonOrgUnitData: OrganizationUnit[],
  jsonPrimaryUnitData: OrganizationUnit[],
  jsonOrgUnitDataInclDeleted: OrganizationUnit[],
  jsonOffices: Office[],
  t: TFunction,
  jsonLegalEntities: LegalEntity[],
  jsonCostCenters: CostCenter[],
  employeeDataJson: Employee,
  jsonContractEmploymentData: FullEmploymentData,
  jsonContractDevHistoricalData: EmplHistoryType[],
  jsonContractDevCurrentData: EmplHistoryType[],
  jsonContractCustomDataPeople: CustomFieldValues,
  jsonCustomContractEmployeeFieldAttr: FieldAttributes[],
  jsonCustomContractEmployeeFieldsTranslation: Enum[][],
  jsonContractCustomDataPosition: CustomFieldValues,
  jsonCustomContractPositionFieldAttr: FieldAttributes[],
  jsonCustomContractPositionFieldsTranslation: Enum[][],
  jsonShortAbsences: ShortAbsence[],
  jsonLongAbsences: LongAbsence[],
  jsonDevOtherExperienceData: EmployeeOtherHistoryType[],
) => {
  const jsonContractEmployeeData = { ...employeeDataJson };
  if (selectedEmployment && jsonContractEmployeeData.empPosition) {
    jsonContractEmployeeData.empPosition = {
      ...jsonContractEmployeeData.empPosition,
      ...selectedEmployment?.empPosition,
    };
  }
  if (jsonContractEmploymentData) {
    jsonContractEmploymentData.emplBasic = {
      ...jsonContractEmploymentData.emplBasic,
      ...selectedEmployment?.employment,
      fEmplDateStart: new Date(
        selectedEmployment?.employment?.fEmplDateStart ||
          jsonContractEmploymentData.emplBasic.fEmplDateStart,
      ),
      fEmplDateArrival: new Date(
        selectedEmployment?.employment?.fEmplDateArrival ||
          jsonContractEmploymentData.emplBasic.fEmplDateArrival,
      ),
      fEmplTrialPeriodEndDate: new Date(
        selectedEmployment?.employment?.fEmplTrialPeriodEndDate ||
          jsonContractEmploymentData.emplBasic.fEmplTrialPeriodEndDate,
      ),
      fEmplEnteredTime: new Date(
        selectedEmployment?.employment?.fEmplEnteredTime ||
          jsonContractEmploymentData.emplBasic.fEmplEnteredTime,
      ),
    };
    jsonContractEmploymentData.emplDetail[0] = {
      ...jsonContractEmploymentData.emplDetail[0],
      ...selectedEmployment?.employmentDetails,
      fEmplDetailStartDate: new Date(
        selectedEmployment?.employmentDetails?.fEmplDetailStartDate ||
          jsonContractEmploymentData.emplDetail[0].fEmplDetailStartDate,
      ),
      fEmplSubEnteredTime: new Date(
        selectedEmployment?.employmentDetails?.fEmplSubEnteredTime ||
          jsonContractEmploymentData.emplDetail[0].fEmplSubEnteredTime,
      ),
    };
  }

  return Promise.all([
    jsonContractEmployeeData.hasOwnProperty('empPosition') &&
    jsonContractEmployeeData.empPosition.hasOwnProperty(
      'fInternalPositionManager',
    ) &&
    jsonContractEmployeeData.empPosition.fInternalPositionManager !== -1
      ? getEmployeeData(
          jsonContractEmployeeData.empPosition.fInternalPositionManager,
        )
      : undefined,
    jsonContractEmployeeData.hasOwnProperty('empPositionPending') &&
    jsonContractEmployeeData.empPositionPending?.hasOwnProperty(
      'fInternalPositionManager',
    ) &&
    jsonContractEmployeeData.empPositionPending.fInternalPositionManager !== -1
      ? getEmployeeData(
          jsonContractEmployeeData.empPositionPending.fInternalPositionManager,
        )
      : undefined,
  ])
    .then((extraData) => {
      const jsonManagerData = extraData[0];
      const jsonPendingManagerData = extraData[1];
      const data = {
        ...{ Today: moment(new Date().toISOString()).format('DD-MM-YYYY') },
        ...{
          Employee: getEmployeeDataPlaceholders(
            jsonContractEmployeeData,
            jsonShortAbsences ?? [],
            jsonLongAbsences ?? [],
            jsonContractEmploymentData as FullEmploymentData,
            jsonContractDevHistoricalData as EmplHistoryType[],
            jsonContractDevCurrentData as EmplHistoryType[],
            jsonContractCustomDataPeople,
            jsonCustomContractEmployeeFieldAttr,
            jsonCustomContractEmployeeFieldsTranslation as Enum[][],
            jsonContractCustomDataPosition,
            jsonCustomContractPositionFieldAttr,
            jsonCustomContractPositionFieldsTranslation as Enum[][],
            jsonDevOtherExperienceData as EmployeeOtherHistoryType[],
            jsonEnumALL,
            jsonAllEmp,
            isCustomDataEnabled,
            t,
            jsonOffices,
            jsonLegalEntities,
            jsonOrgUnitDataInclDeleted,
            jsonOrgUnitData,
            jsonCostCenters,
          ),
        },
        ...{
          Manager: getManagerDataPlaceholders(
            jsonContractEmployeeData,
            jsonManagerData,
            jsonPendingManagerData,
            jsonAllEmp,
            jsonPrimaryUnitData,
            jsonOffices,
          ),
        },
      };

      return data;
    })
    .catch((err) => {
      console.log('printDocx', err);
    });
};

export const getEmployeeDataPlaceholders = (
  jsonContractEmployeeData: Employee,
  jsonShortAbsences: ShortAbsence[],
  jsonLongAbsences: LongAbsence[],
  jsonContractEmploymentData: FullEmploymentData,
  jsonContractDevHistoricalData: EmplHistoryType[],
  jsonContractDevCurrentData: EmplHistoryType[],
  jsonContractCustomDataPeople: CustomFieldValues | undefined,
  jsonCustomContractEmployeeFieldAttr: FieldAttributes[],
  jsonCustomContractEmployeeFieldsTranslation: Enum[][],
  jsonContractCustomDataPosition: CustomFieldValues | undefined,
  jsonCustomContractPositionFieldAttr: FieldAttributes[],
  jsonCustomContractPositionFieldsTranslation: Enum[][],
  jsonDevOtherExperienceData: EmployeeOtherHistoryType[],
  jsonEnumALL: AllEnums,
  jsonAllEmp: EmployeesIndexed,
  isCustomDataEnabled: boolean,
  t: TFunction,
  jsonOffices: Office[],
  jsonLegalEntities: LegalEntity[],
  jsonOrgUnitDataInclDeleted: OrganizationUnit[],
  jsonOrgUnitData: OrganizationUnit[],
  jsonCostCenters: CostCenter[],
) => {
  const pendingEmployeePosition = _.get(
    jsonContractEmployeeData,
    'empPositionPending',
  );
  const currentEmployeePosition = _.get(
    jsonContractEmployeeData,
    'empPosition',
  );
  const employeePosition = pendingEmployeePosition || currentEmployeePosition;

  const pendingEmploymentDetails = _.find(
    _.get(jsonContractEmploymentData, 'emplDetail') || [],
    { fEmplDetailIsPending: true },
  );
  const currentEmploymentDetails = _.find(
    _.get(jsonContractEmploymentData, 'emplDetail') || [],
    { fEmplDetailIsPending: false },
  );
  const employmentDetails =
    pendingEmploymentDetails || currentEmploymentDetails;

  const employmentSalaryProposal = getEmploymentSalaryProposal(
    jsonContractEmploymentData,
    jsonEnumALL,
    jsonAllEmp,
  );
  const currentEmploymentSalary = getEmploymentSalary(
    jsonContractEmploymentData,
    jsonEnumALL,
  );

  const pendingWorkingHours = _.find(
    _.get(jsonContractEmploymentData, 'emplWorkingHours'),
    { fWorkHoursIsPending: true },
  );
  const currentWorkingHours = _.find(
    _.get(jsonContractEmploymentData, 'emplWorkingHours'),
    { fWorkHoursIsPending: false },
  );
  const workingHours = pendingWorkingHours || currentWorkingHours;

  return {
    BasicData: getEmployeeBasicData(jsonContractEmployeeData, jsonEnumALL, t),
    Position: getEmployeePositionData(
      employeePosition,
      jsonEnumALL,
      jsonLegalEntities,
      jsonOrgUnitData,
      jsonCostCenters,
    ),
    PendingPosition:
      pendingEmployeePosition &&
      getEmployeePositionData(
        pendingEmployeePosition,
        jsonEnumALL,
        jsonLegalEntities,
        jsonOrgUnitData,
        jsonCostCenters,
      ),
    CurrentPosition: getEmployeePositionData(
      currentEmployeePosition,
      jsonEnumALL,
      jsonLegalEntities,
      jsonOrgUnitData,
      jsonCostCenters,
    ),
    Employment: getEmploymentBasicData(
      jsonContractEmploymentData,
      jsonEnumALL,
      t,
    ),
    EmploymentDetails: getEmploymentDetails(employmentDetails, jsonEnumALL),
    PendingEmploymentDetails:
      pendingEmploymentDetails &&
      getEmploymentDetails(pendingEmploymentDetails, jsonEnumALL),
    CurrentEmploymentDetails: getEmploymentDetails(
      currentEmploymentDetails,
      jsonEnumALL,
    ),
    Salary: employmentSalaryProposal || currentEmploymentSalary,
    CurrentSalary: currentEmploymentSalary,
    SalaryProposal: employmentSalaryProposal,
    WorkingHours: getEmploymentWorkingHours(workingHours),
    PendingWorkingHours:
      pendingWorkingHours && getEmploymentWorkingHours(pendingWorkingHours),
    CurrentWorkingHours: getEmploymentWorkingHours(currentWorkingHours),
    Benefits: getEmploymentBenefits(jsonContractEmploymentData, jsonEnumALL),
    BenefitProposals: getEmploymentBenefitProposals(
      jsonContractEmploymentData,
      jsonEnumALL,
    ),
    Allowances: getEmploymentAllowances(
      jsonContractEmploymentData,
      jsonEnumALL,
    ),
    AllowanceProposals: getEmploymentAllowanceProposals(
      jsonContractEmploymentData,
      jsonEnumALL,
    ),
    BankAccount: getEmploymentDefaultBankAccount(jsonContractEmploymentData),
    ContractHistory: getEmployeeContractHistory(
      jsonContractDevHistoricalData,
      jsonContractDevCurrentData,
      jsonEnumALL,
      jsonAllEmp,
      jsonLegalEntities,
      jsonOrgUnitDataInclDeleted,
    ),
    Contacts: getEmployeeContacts(jsonContractEmployeeData, jsonOffices),
    ShortAbsences: getEmployeeShortAbsences(jsonShortAbsences, jsonEnumALL),
    LongAbsences: getEmployeeLongAbsences(jsonLongAbsences, jsonEnumALL),
    CustomData: getEmployeeCustomData(
      'EMPLOYEE',
      jsonContractCustomDataPeople,
      jsonCustomContractEmployeeFieldAttr,
      jsonCustomContractEmployeeFieldsTranslation,
      isCustomDataEnabled,
      jsonEnumALL,
      t,
    ),
    JobDescription: getEmployeeCustomData(
      'EMPLOYEE_POS',
      jsonContractCustomDataPosition,
      jsonCustomContractPositionFieldAttr,
      jsonCustomContractPositionFieldsTranslation,
      isCustomDataEnabled,
      jsonEnumALL,
      t,
    ),
    OtherExperience: getEmployeeOtherExperience(jsonDevOtherExperienceData),
  };
};

const getEmployeeOtherExperience = (
  jsonDevOtherExperienceData: EmployeeOtherHistoryType[],
) => {
  return jsonDevOtherExperienceData?.map((experience) => {
    return {
      Title: _.get(experience, 'fOtherPositionTitle'),
      SubTitle: _.get(experience, 'fOtherPositionSubtitle'),
      StartDate: formatIfNotUndefined(
        _.get(experience, 'fOtherPositionStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      EndDate: formatIfNotUndefined(
        _.get(experience, 'fOtherPositionEndDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
    };
  });
};

const formatIfNotUndefined = <X, Y>(value: X, formatter: (value: X) => Y) => {
  if (_.isUndefined(value)) {
    return '';
  }

  return formatter(value);
};

export const getManagerDataPlaceholders = (
  jsonContractEmployeeData: Employee,
  jsonManagerData: Employee | undefined,
  jsonPendingManagerData: Employee | undefined,
  jsonAllEmp: EmployeesIndexed,
  jsonPrimaryUnitData: OrganizationUnit[],
  jsonOffices: Office[],
) => {
  return _.assign(
    {},
    (() => {
      const position =
        _.get(jsonContractEmployeeData, 'empPositionPending') ||
        _.get(jsonContractEmployeeData, 'empPosition');

      const internalPositionManager = _.get(
        position,
        'fInternalPositionManager',
      );
      const internalPositionUnit = _.get(position, 'fInternalPositionUnit');

      if (internalPositionManager !== -1) {
        return {
          Name: jsPersonName(internalPositionManager, jsonAllEmp),
          Position: jsonAllEmp[internalPositionManager].fEmpPosition,
        };
      }

      if (internalPositionUnit !== -1) {
        const unit = _.find(jsonPrimaryUnitData, {
          fOrgUnitId: internalPositionUnit,
        });

        if (unit && unit.fOrgUnitManager !== -1) {
          return {
            Name: jsPersonName(unit.fOrgUnitManager, jsonAllEmp),
            Position: jsonAllEmp[unit.fOrgUnitManager].fEmpPosition,
          };
        }
      }

      return {
        Name: '',
        Position: '',
      };
    })(),
    {
      Location: formatIfNotUndefined(
        _.get(
          jsonPendingManagerData || jsonManagerData,
          'empWorkContact.fEmpOfficeLoc',
        ),
        (value) => jsOfficeName(value || -1, jsonOffices),
      ),
    },
  );
};

const getEmployeeBasicData = (
  jsonContractEmployeeData: Employee,
  jsonEnumALL: AllEnums,
  t: TFunction,
) => {
  return {
    Id: _.get(jsonContractEmployeeData, 'empBasicData.fEmpId'),
    SSN: _.get(jsonContractEmployeeData, 'empBasicData.fEmpSocSecNum'),
    PersonalNumber: _.get(
      jsonContractEmployeeData,
      'empBasicData.fEmpPersonNumber',
    ),
    Name:
      _.get(jsonContractEmployeeData, 'empBasicData.fEmpFirstName') +
      ' ' +
      _.get(jsonContractEmployeeData, 'empBasicData.fEmpLastName'),
    FirstName: _.get(jsonContractEmployeeData, 'empBasicData.fEmpFirstName'),
    LastName: _.get(jsonContractEmployeeData, 'empBasicData.fEmpLastName'),
    GivenNames: _.get(jsonContractEmployeeData, 'empBasicData.fEmpGivenNames'),
    DateOfBirth: formatIfNotUndefined(
      _.get(jsonContractEmployeeData, 'empBasicData.fEmpDateOfBirth'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    Gender: formatIfNotUndefined(
      _.get(jsonContractEmployeeData, 'empBasicData.fEmpGender'),
      (v) => {
        return t(v || '');
      },
    ),
    Info: _.get(jsonContractEmployeeData, 'empBasicData.fEmpInfo'),
    Nationality: formatIfNotUndefined(
      _.get(jsonContractEmployeeData, 'empBasicData.fEmpNationality'),
      (v) => {
        return jsSelectName(jsonEnumALL.COUNTRY, v);
      },
    ),
    Status: formatIfNotUndefined(
      _.get(jsonContractEmployeeData, 'empBasicData.fEmpStatus'),
      (v) => {
        return jsSelectName(jsonEnumALL.EMPLOYEE_STATUS, v);
      },
    ),
    OriginalStartDate: formatIfNotUndefined(
      _.get(jsonContractEmployeeData, 'empBasicData.fEmpOrigStartDate'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
  };
};

const getEmployeePositionData = (
  position: EmpPosition,
  jsonEnumALL: AllEnums,
  jsonLegalEntities: LegalEntity[],
  jsonOrgUnitData: OrganizationUnit[],
  jsonCostCenters: CostCenter[],
) => {
  return {
    Name: formatIfNotUndefined(
      _.get(position, 'fInternalPositionPosition'),
      (v) => {
        return jsSelectName(jsonEnumALL.EMPLOYEE_POSITION, v);
      },
    ),
    StartDate: formatIfNotUndefined(
      _.get(position, 'fInternalPositionStartDate'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    JobTitle: _.get(position, 'fEmpJobTitle'),
    CostCenter: formatIfNotUndefined(
      _.get(position, 'fEmpCostCenters'),
      (v) => {
        const defaultCC =
          _.find(v, { fEmpCostCenterIsDefault: true }) ||
          ({} as EmployeeCostCenter);
        return jsCCName(defaultCC.fEmpCostCenter, jsonCostCenters);
      },
    ),
    LegalEntityCode: formatIfNotUndefined(
      _.get(position, 'fEmpLegalEntity'),
      (v) => {
        return jsLECode(v, jsonLegalEntities);
      },
    ),
    LegalEntity: formatIfNotUndefined(
      _.get(position, 'fEmpLegalEntity'),
      (v) => {
        return jsLEName(v, jsonLegalEntities);
      },
    ),
    Unit: formatIfNotUndefined(
      _.get(position, 'fInternalPositionUnit'),
      (v) => {
        return jsOrgUnitName(v, jsonOrgUnitData);
      },
    ),
    Info: _.get(position, 'fInternalPositionInfo'),
  };
};

const getEmploymentBasicData = (
  jsonContractEmploymentData: FullEmploymentData,
  jsonEnumALL: AllEnums,
  t: TFunction,
) => {
  const jsonTrialPeriodOptions = [
    { code: 'P1M', name: '1 ' + t('laMonth') },
    { code: 'P2M', name: '2 ' + t('laMonths') },
    { code: 'P3M', name: '3 ' + t('laMonths') },
    { code: 'P4M', name: '4 ' + t('laMonths') },
    { code: 'P5M', name: '5 ' + t('laMonths') },
    { code: 'P6M', name: '6 ' + t('laMonths') },
  ];
  return {
    Type: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplType'),
      (v) => {
        return jsSelectName(jsonEnumALL.EMPLOYMENT_TYPE, v);
      },
    ),
    Currency: _.get(jsonContractEmploymentData, 'emplBasic.fEmplCurrency'),
    StartDate: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplDateStart'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    ArrivalDate: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplDateArrival'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    EndDate: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplLastDateEmployment'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    TerminationDate: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplTerminationDate'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    PlannedEndDate: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplPlannedEndDate'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    TrialPeriod: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplTrialPeriod'),
      (v) => {
        return jsSelectName(jsonTrialPeriodOptions, v);
      },
    ),
    TrialPeriodEndDate: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplTrialPeriodEndDate'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    Info: _.get(jsonContractEmploymentData, 'emplBasic.fEmplInfo') || '',
    TerminationReason: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplTerminationReason'),
      (v) => {
        return jsSelectName(jsonEnumALL.EMPL_TERMINATION_REASON, v);
      },
    ),
    FixedTermReason: formatIfNotUndefined(
      _.get(jsonContractEmploymentData, 'emplBasic.fEmplFixedTermReason'),
      (v) => {
        return jsSelectName(jsonEnumALL.EMPL_FIXED_TERM_REASON, v);
      },
    ),
  };
};

const getEmploymentDetails = (
  employmentDetails: EmploymentDetailData | undefined,
  jsonEnumALL: AllEnums,
) => {
  return _.assign(
    {
      Info: _.get(employmentDetails, 'fEmplDetailInfo'),
      JobGrade: formatIfNotUndefined(
        _.get(employmentDetails, 'fEmplDetailJobGrade'),
        (v) => {
          return jsSelectName(jsonEnumALL.JOB_GRADE, v);
        },
      ),
      TaxNumber: _.get(employmentDetails, 'fEmplDetailTaxNumber'),
      StatusCode: _.get(employmentDetails, 'fEmplDetailStatus'),
      Status: formatIfNotUndefined(
        _.get(employmentDetails, 'fEmplDetailStatus'),
        (v) => {
          return jsSelectName(jsonEnumALL.EMPLOYMENT_STATUS, v);
        },
      ),
      StartDate: formatIfNotUndefined(
        _.get(employmentDetails, 'fEmplDetailStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
    },
    [1, 2, 3, 4, 5].reduce((acc, n) => {
      const obj = {} as Record<`PersonnelGroup${number}`, string | undefined>;
      obj[('PersonnelGroup' + n) as `PersonnelGroup${number}`] =
        formatIfNotUndefined(
          _.get(employmentDetails, 'fEmplDetailPersonnelGroup' + n),
          (v) => {
            return jsSelectName(jsonEnumALL['PERSONNEL_GROUP_' + n], v);
          },
        );

      return _.assign(acc, obj);
    }, {}),
  );
};

const getEmploymentWorkingHours = (
  workingHours: EmploymentWorkingHours | undefined,
) => {
  return {
    FT: formatIfNotUndefined(
      _.get(workingHours, 'fWorkHoursFtHoursPerWeek'),
      (v) => {
        return parseFloat(String(v)).toFixed(2);
      },
    ),
    PT: formatIfNotUndefined(
      _.get(workingHours, 'fWorkHoursPtHoursPerWeek'),
      (v) => {
        return parseFloat(String(v)).toFixed(2);
      },
    ),
    PTPercentage: formatIfNotUndefined(
      _.get(workingHours, 'fWorkHoursPtPercentage'),
      (v) => {
        return parseFloat(String(v)).toFixed(2);
      },
    ),
    StartDate: formatIfNotUndefined(
      _.get(workingHours, 'fWorkHoursStartDate'),
      (v) => {
        return moment(v).format('DD-MM-YYYY');
      },
    ),
    Reason: _.get(workingHours, 'fWorkHoursPtReason'),
    Info: _.get(workingHours, 'fWorkHoursInfo'),
    Type: _.get(workingHours, 'fWorkHoursType'),
  };
};

const getEmploymentBenefits = (
  jsonContractEmploymentData: FullEmploymentData,
  jsonEnumALL: AllEnums,
) => {
  return (_.get(jsonContractEmploymentData, 'emplBenefits') || []).map(
    (benefit) => {
      return {
        Type: formatIfNotUndefined(_.get(benefit, 'fBenefitType'), (v) => {
          return jsSelectName(jsonEnumALL.BENEFIT_TYPE, v);
        }),
        StartDate: formatIfNotUndefined(
          _.get(benefit, 'fBenefitStartDate'),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        ),
        EndDate: formatIfNotUndefined(
          _.get(benefit, 'fBenefitEndDate'),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        ),
        Frequency: formatIfNotUndefined(
          _.get(benefit, 'fBenefitFrequency'),
          (v) => {
            return jsSelectName(jsonEnumALL.EVENT_FREQUENCY, v);
          },
        ),
        Amount: formatIfNotUndefined(_.get(benefit, 'fBenefitAmount'), (v) => {
          return parseFloat(String(v)).toFixed(2);
        }),
        TaxableAmount: formatIfNotUndefined(
          _.get(benefit, 'fBenefitTaxableAmount'),
          (v) => {
            return parseFloat(String(v)).toFixed(2);
          },
        ),
      };
    },
  );
};

const getEmploymentBenefitProposals = (
  jsonContractEmploymentData: FullEmploymentData,
  jsonEnumALL: AllEnums,
) => {
  return (_.get(jsonContractEmploymentData, 'emplBenefitProposals') || []).map(
    (benefit) => {
      return {
        Status: formatIfNotUndefined(
          _.get(benefit, 'fBenefitProposalStatus'),
          (v) => {
            return jsSelectName(jsonEnumALL.WORKFLOW_ITEM_STATUS, v);
          },
        ),
        Type: formatIfNotUndefined(
          _.get(benefit, 'fBenefitProposalType'),
          (v) => {
            return jsSelectName(jsonEnumALL.BENEFIT_TYPE, v);
          },
        ),
        StartDate: formatIfNotUndefined(
          _.get(benefit, 'fBenefitProposalStartDate'),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        ),
        EndDate: formatIfNotUndefined(
          _.get(benefit, 'fBenefitProposalEndDate'),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        ),
        Frequency: formatIfNotUndefined(
          _.get(benefit, 'fBenefitProposalFrequency'),
          (v) => {
            return jsSelectName(jsonEnumALL.EVENT_FREQUENCY, v);
          },
        ),
        Amount: formatIfNotUndefined(
          _.get(benefit, 'fBenefitProposalAmount'),
          (v) => {
            return parseFloat(String(v)).toFixed(2);
          },
        ),
        TaxableAmount: formatIfNotUndefined(
          _.get(benefit, 'fBenefitProposalTaxableAmount'),
          (v) => {
            return parseFloat(String(v)).toFixed(2);
          },
        ),
      };
    },
  );
};

const getEmploymentAllowances = (
  jsonContractEmploymentData: FullEmploymentData,
  jsonEnumALL: AllEnums,
) => {
  return (_.get(jsonContractEmploymentData, 'emplAllowances') || []).map(
    (allowance) => {
      return {
        Type: formatIfNotUndefined(_.get(allowance, 'fAllowanceType'), (v) => {
          return jsSelectName(jsonEnumALL.ALLOWANCE_TYPE, v);
        }),
        StartDate: formatIfNotUndefined(
          _.get(allowance, 'fAllowanceStartDate'),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        ),
        EndDate: formatIfNotUndefined(
          _.get(allowance, 'fAllowanceEndDate'),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        ),
        Frequency: formatIfNotUndefined(
          _.get(allowance, 'fAllowanceFrequency'),
          (v) => {
            return jsSelectName(jsonEnumALL.EVENT_FREQUENCY, v);
          },
        ),
        Amount: formatIfNotUndefined(
          _.get(allowance, 'fAllowanceAmount'),
          (v) => {
            return parseFloat(String(v)).toFixed(2);
          },
        ),
        TaxableAmount: formatIfNotUndefined(
          _.get(allowance, 'fAllowanceTaxableAmount'),
          (v) => {
            return parseFloat(String(v)).toFixed(2);
          },
        ),
      };
    },
  );
};

const getEmploymentAllowanceProposals = (
  jsonContractEmploymentData: FullEmploymentData,
  jsonEnumALL: AllEnums,
) => {
  return (
    _.get(jsonContractEmploymentData, 'emplAllowanceProposals') || []
  ).map((allowance) => {
    return {
      Status: formatIfNotUndefined(
        _.get(allowance, 'fAllowanceProposalStatus'),
        (v) => {
          return jsSelectName(jsonEnumALL.WORKFLOW_ITEM_STATUS, v);
        },
      ),
      Type: formatIfNotUndefined(
        _.get(allowance, 'fAllowanceProposalType'),
        (v) => {
          return jsSelectName(jsonEnumALL.ALLOWANCE_TYPE, v);
        },
      ),
      StartDate: formatIfNotUndefined(
        _.get(allowance, 'fAllowanceProposalStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      EndDate: formatIfNotUndefined(
        _.get(allowance, 'fAllowanceProposalEndDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      Frequency: formatIfNotUndefined(
        _.get(allowance, 'fAllowanceProposalFrequency'),
        (v) => {
          return jsSelectName(jsonEnumALL.EVENT_FREQUENCY, v);
        },
      ),
      Amount: formatIfNotUndefined(
        _.get(allowance, 'fAllowanceProposalAmount'),
        (v) => {
          return parseFloat(String(v)).toFixed(2);
        },
      ),
      TaxableAmount: formatIfNotUndefined(
        _.get(allowance, 'fAllowanceProposalTaxableAmount'),
        (v) => {
          return parseFloat(String(v)).toFixed(2);
        },
      ),
    };
  });
};

const getEmploymentDefaultBankAccount = (
  jsonContractEmploymentData: FullEmploymentData,
) => {
  const defaultBankAccount = _.find(
    _.get(jsonContractEmploymentData, 'emplBankAccounts'),
    { fBankAccountIsDefault: true },
  );
  return _.get(defaultBankAccount, 'fBankAccountIBAN') || '';
};

const getEmployeeContractHistory = (
  jsonContractDevHistoricalData: EmplHistoryType[],
  jsonContractDevCurrentData: EmplHistoryType[],
  jsonEnumALL: AllEnums,
  jsonAllEmp: EmployeesIndexed,
  jsonLegalEntities: LegalEntity[],
  jsonOrgUnitDataInclDeleted: OrganizationUnit[],
) => {
  const sortedDevHistory = _.sortBy(
    jsonContractDevHistoricalData?.concat(jsonContractDevCurrentData),
    'fInternalPositionStartDate',
  ).reverse();

  return sortedDevHistory.map((item) => {
    return {
      StartDate: formatIfNotUndefined(
        _.get(item, 'fInternalPositionStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      EndDate: formatIfNotUndefined(
        _.get(item, 'fInternalPositionEndDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      JobTitle: _.get(item, 'fEmpJobTitle'),
      Position: formatIfNotUndefined(
        _.get(item, 'fInternalPositionPosition'),
        (v) => {
          return jsSelectName(jsonEnumALL.EMPLOYEE_POSITION, v);
        },
      ),
      Unit: formatIfNotUndefined(_.get(item, 'fInternalPositionUnit'), (v) => {
        return jsOrgUnitNameInclDeleted(+v, jsonOrgUnitDataInclDeleted);
      }),
      Manager: formatIfNotUndefined(
        _.get(item, 'fInternalPositionManager'),
        (v) => {
          return jsPersonName(v, jsonAllEmp);
        },
      ),
      Info: _.get(item, 'fInternalPositionInfo') || '',
      LegalEntityCode: formatIfNotUndefined(
        _.get(item, 'fEmpLegalEntity'),
        (v) => {
          return v;
        },
      ),
      LegalEntity: formatIfNotUndefined(_.get(item, 'fEmpLegalEntity'), (v) => {
        return jsLEName(v, jsonLegalEntities);
      }),
    };
  });
};

const getEmployeeContacts = (
  jsonContractEmployeeData: Employee,
  jsonOffices: Office[],
) => {
  return {
    Work: (() => {
      const office = jsOffice(
        _.get(jsonContractEmployeeData, 'empWorkContact.fEmpOfficeLoc'),
        jsonOffices,
      );

      return {
        Location: (office && _.get(office, 'fOfficeName')) || '',
        Address1: (office && _.get(office, 'fOfficeAddress1')) || '',
        Address2: (office && _.get(office, 'fOfficeAddress2')) || '',
        PostalCode: (office && _.get(office, 'fOfficePostalCode')) || '',
        City: (office && _.get(office, 'fOfficeCity')) || '',
        Country: (office && _.get(office, 'fOfficeCountry')) || '',
        Floor: _.get(
          jsonContractEmployeeData,
          'empWorkContact.fEmpOfficeFloor',
        ),
        Room: _.get(jsonContractEmployeeData, 'empWorkContact.fEmpOfficeRoom'),
        Email: _.get(jsonContractEmployeeData, 'empWorkContact.fEmpEmailWork'),
        Phone: _.get(jsonContractEmployeeData, 'empWorkContact.fEmpPhoneWork'),
        MobilePhone: _.get(
          jsonContractEmployeeData,
          'empWorkContact.fEmpPhoneMob',
        ),
      };
    })(),
    Emergency: {
      Name: _.get(jsonContractEmployeeData, 'empEmeContact.fEmpEmeName'),
      Phone: _.get(jsonContractEmployeeData, 'empEmeContact.fEmpEmePhone'),
      Email: _.get(jsonContractEmployeeData, 'empEmeContact.fEmpEmeEmail'),
    },
    Home: {
      Address1:
        _.get(jsonContractEmployeeData, 'empHomeContact.fEmpStreetAddress1') ||
        '',
      Address2:
        _.get(jsonContractEmployeeData, 'empHomeContact.fEmpStreetAddress2') ||
        '',
      PostalCode:
        _.get(jsonContractEmployeeData, 'empHomeContact.fEmpPostalCode') || '',
      City: _.get(jsonContractEmployeeData, 'empHomeContact.fEmpCity') || '',
      State: _.get(jsonContractEmployeeData, 'empHomeContact.fEmpState'),
      Country: _.get(jsonContractEmployeeData, 'empHomeContact.fEmpCountry'),
      Email: _.get(jsonContractEmployeeData, 'empHomeContact.fEmpEmailHome'),
      Phone: _.get(jsonContractEmployeeData, 'empHomeContact.fEmpPhoneHome'),
    },
    Other: {
      Address1: _.get(
        jsonContractEmployeeData,
        'empOtherContact.fEmpStreetAddress1',
      ),
      Address2: _.get(
        jsonContractEmployeeData,
        'empOtherContact.fEmpStreetAddress2',
      ),
      PostalCode: _.get(
        jsonContractEmployeeData,
        'empOtherContact.fEmpPostalCode',
      ),
      City: _.get(jsonContractEmployeeData, 'empOtherContact.fEmpCity'),
      State: _.get(jsonContractEmployeeData, 'empOtherContact.fEmpState'),
      Country: _.get(jsonContractEmployeeData, 'empOtherContact.fEmpCountry'),
      Email: _.get(jsonContractEmployeeData, 'empOtherContact.fEmpEmailOther'),
      Phone: _.get(jsonContractEmployeeData, 'empOtherContact.fEmpPhoneOther'),
    },
  };
};

const getEmployeeShortAbsences = (
  jsonShortAbsences: ShortAbsence[],
  jsonEnumALL: AllEnums,
) => {
  return (jsonShortAbsences || []).map((absence) => {
    return {
      StartDate: formatIfNotUndefined(
        _.get(absence, 'fAbsenceShortStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      EndDate: formatIfNotUndefined(
        _.get(absence, 'fAbsenceShortEndDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      Days: _.get(absence, 'fAbsenceShortDays'),
      Status: jsSelectName(
        jsonEnumALL.ABSENCE_STATUS,
        _.get(absence, 'fAbsenceShortStatus'),
      ),
      Reason: jsSelectName(
        jsonEnumALL.ABSENCE_SHORT_REASON,
        _.get(absence, 'fAbsenceShortReason'),
      ),
    };
  });
};

const getEmployeeLongAbsences = (
  jsonLongAbsences: LongAbsence[],
  jsonEnumALL: AllEnums,
) => {
  return (jsonLongAbsences || []).map((absence) => {
    return {
      StartDate: formatIfNotUndefined(
        _.get(absence, 'fAbsenceLongStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      EndDate: formatIfNotUndefined(
        _.get(absence, 'fAbsenceLongEndDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      Status: jsSelectName(
        jsonEnumALL.ABSENCE_STATUS,
        _.get(absence, 'fAbsenceLongStatus'),
      ),
      Reason: jsSelectName(
        jsonEnumALL.ABSENCE_LONG_REASON,
        _.get(absence, 'fAbsenceLongReason'),
      ),
    };
  });
};

const getEmployeeCustomData = (
  key: string,
  data: CustomFieldValues | undefined,
  fields: FieldAttributes[],
  trs: Enum[][],
  isCustomDataEnabled: boolean,
  jsonEnumALL: AllEnums,
  t: TFunction,
) => {
  if (!isCustomDataEnabled) {
    return {};
  }

  const indexedFieldAttributes = fields.reduce<{
    [name: string]: FieldAttributes;
  }>((acc, field) => {
    return { ...acc, [field.fCustomDataAttributeName]: field };
  }, {});

  return trs.flat().reduce((acc, tr) => {
    return {
      ...acc,
      [toPascalCase(tr.name)]: getCustomFieldValue(
        key,
        data,
        indexedFieldAttributes[tr.code],
        jsonEnumALL,
        t,
      ),
    };
  }, {});
};

const getEmploymentSalaryProposal = (
  jsonContractEmploymentData: FullEmploymentData,
  jsonEnumALL: AllEnums,
  jsonAllEmp: EmployeesIndexed,
) => {
  const salary = _.get(jsonContractEmploymentData, 'emplSalaryProposals[0]');

  if (!salary) {
    return undefined;
  }

  return _.assign(
    {
      Value: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalSalary'),
        (v) => {
          return parseFloat(String(v)).toFixed(2);
        },
      ),
      Frequency: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalFrequency'),
        (v) => {
          return jsSelectName(jsonEnumALL.EVENT_FREQUENCY, v);
        },
      ),
      Info: _.get(salary, 'fSalaryProposalInfo'),
      Type: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalSalaryType'),
        (v) => {
          return jsSelectName(jsonEnumALL.REW_SALARY_TYPE, v);
        },
      ),
      Class: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalSalaryClass'),
        (v) => {
          return jsSelectName(jsonEnumALL.REW_SALARY_CLASS, v);
        },
      ),
      StartDate: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      EndDate: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalEndDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      Change: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalChange'),
        (v) => {
          return parseFloat(String(v)).toFixed(2);
        },
      ),
      ChangePct: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalChangePct'),
        (v) => {
          return parseFloat(String(v)).toFixed(2);
        },
      ),
      ChangeReason: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalChangeReason'),
        (v) => {
          return jsSelectName(jsonEnumALL.SALARY_CHANGE_REASON, v);
        },
      ),
      Manager: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalManagerId'),
        (v) => {
          return jsPersonName(v, jsonAllEmp);
        },
      ),
      HigherManager: formatIfNotUndefined(
        _.get(salary, 'fSalaryProposalHigherMgrId'),
        (v) => {
          return jsPersonName(v, jsonAllEmp);
        },
      ),
      HR: formatIfNotUndefined(_.get(salary, 'fSalaryProposalHrId'), (v) => {
        return jsPersonName(v, jsonAllEmp);
      }),
    },
    [1, 2, 3, 4].reduce((acc, n) => {
      const obj: Record<
        `Comp${number}Amount` | `Comp${number}StartDate`,
        string
      > = {};

      obj[('Comp' + n + 'Amount') as `Comp${number}Amount`] =
        formatIfNotUndefined(
          _.get(salary, 'fSalaryProposalComponents.fSalaryComp' + n + 'Amount'),
          (v) => {
            return parseFloat(v).toFixed(2);
          },
        );

      obj[('Comp' + n + 'StartDate') as `Comp${number}StartDate`] =
        formatIfNotUndefined(
          _.get(
            salary,
            'fSalaryProposalComponents.fSalaryComp' + n + 'StartDate',
          ),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        );

      return _.assign(acc, obj);
    }, {}),
  );
};

const getEmploymentSalary = (
  jsonContractEmploymentData: FullEmploymentData,
  jsonEnumALL: AllEnums,
) => {
  const salary = _.get(jsonContractEmploymentData, 'emplSalaries[0]');

  return _.assign(
    {
      Value: formatIfNotUndefined(_.get(salary, 'fSalaryBaseSalary'), (v) => {
        return parseFloat(String(v)).toFixed(2);
      }),
      Frequency: formatIfNotUndefined(
        _.get(salary, 'fSalaryFrequency'),
        (v) => {
          return jsSelectName(jsonEnumALL.EVENT_FREQUENCY, v);
        },
      ),
      Info: _.get(salary, 'fSalaryInfo'),
      Type: formatIfNotUndefined(_.get(salary, 'fSalaryType'), (v) => {
        return jsSelectName(jsonEnumALL.REW_SALARY_TYPE, v);
      }),
      Class: formatIfNotUndefined(_.get(salary, 'fSalaryClass'), (v) => {
        return jsSelectName(jsonEnumALL.REW_SALARY_CLASS, v);
      }),
      StartDate: formatIfNotUndefined(
        _.get(salary, 'fSalaryStartDate'),
        (v) => {
          return moment(v).format('DD-MM-YYYY');
        },
      ),
      EndDate: formatIfNotUndefined(_.get(salary, 'fSalaryEndDate'), (v) => {
        return moment(v).format('DD-MM-YYYY');
      }),
    },
    [1, 2, 3, 4].reduce((acc, n) => {
      const obj: {
        [key: `Comp${number}Amount` | `Comp${number}StartDate`]: string;
      } = {};

      obj[('Comp' + n + 'Amount') as `Comp${number}Amount`] =
        formatIfNotUndefined(
          _.get(salary, 'fSalaryComponents.fSalaryComp' + n + 'Amount'),
          (v) => {
            return parseFloat(v).toFixed(2);
          },
        );

      obj[('Comp' + n + 'StartDate') as `Comp${number}StartDate`] =
        formatIfNotUndefined(
          _.get(salary, 'fSalaryComponents.fSalaryComp' + n + 'StartDate'),
          (v) => {
            return moment(v).format('DD-MM-YYYY');
          },
        );

      return _.assign(acc, obj);
    }, {}),
  );
};

export const jsPersonName = (
  strId: number,
  jsonAllEmp: EmployeesIndexed,
  reverse = true,
  delimiter = ', ',
) => {
  const oEmp = jsonAllEmp[Number(strId)];
  if (!oEmp) return '';

  const namesArr = [oEmp.fEmpFirstName, oEmp.fEmpLastName];

  return reverse
    ? namesArr.reverse().join(delimiter)
    : namesArr.join(delimiter);
};

const jsSelectName = (json: Enum[], strId: string | undefined) => {
  try {
    if (strId === undefined || strId === '') return '';
    const oItem = json.find((el) => el.code === strId);
    return oItem === undefined ? strId : oItem.name;
  } catch (err) {}
};

const jsOfficeName = (id: number, jsonOffices: Office[]) => {
  const oOffice = jsonOffices.find((office) => office.fOfficeId === id);
  return oOffice === undefined ? '' : oOffice.fOfficeName;
};

const jsCCName = (
  id: number,
  jsonCostCenters: CostCenter[],
  strPercent?: number,
  hasCustomFields?: boolean,
) => {
  try {
    if (id === undefined) {
      return '';
    }

    const oCC = jsonCostCenters.find((cc) => cc.fCostCenterId === id);
    const customFieldsMarkerHtml = hasCustomFields
      ? '<strong class="cssCustomFieldsMarker">!</strong>'
      : '';

    let percentageHtml = '';
    if (strPercent === undefined || strPercent === 0) {
      percentageHtml = '';
    } else {
      percentageHtml = '(' + strPercent + '%)';
    }

    return oCC === undefined
      ? ''
      : [
          oCC.fCostCenterCode,
          oCC.fCostCenterName,
          percentageHtml,
          customFieldsMarkerHtml,
        ].join(' ');
  } catch (err) {
    console.log(err, 'jsCCName', 'Main');
  }
};

const jsLECode = (strId: number, jsonLegalEntities: LegalEntity[]) => {
  const oLE = jsonLegalEntities.find((le) => le.fLegalEntityId === strId);
  return oLE?.fLegalEntityCode || '';
};

const jsLEName = (strId: number, jsonLegalEntities: LegalEntity[]) => {
  const oLE = jsonLegalEntities.find((le) => le.fLegalEntityId === strId);
  return oLE?.fLegalEntityName || '';
};

const jsOrgUnitName = (id: number, jsonOrgUnitData: OrganizationUnit[]) => {
  const oOU = jsonOrgUnitData.find((unit) => unit.fOrgUnitId === id);
  return oOU === undefined
    ? ''
    : oOU.fOrgUnitName + ' (' + oOU.fOrgUnitNumber + ')';
};

const jsOrgUnitNameInclDeleted = (
  id: number,
  jsonOrgUnitDataInclDeleted: OrganizationUnit[],
) => {
  if (!id) return '';
  const oOU = jsonOrgUnitDataInclDeleted.find((unit) => unit.fOrgUnitId === id);
  return oOU ? oOU.fOrgUnitName + ' (' + oOU.fOrgUnitNumber + ')' : '';
};

const jsOffice = (id: number, jsonOffices: Office[]) =>
  _.find(jsonOffices, { fOfficeId: id }) || '';

const getCustomFieldValue = (
  key: string,
  data: CustomFieldValues | undefined,
  field: FieldAttributes,
  jsonEnumALL: AllEnums,
  t: TFunction,
) => {
  switch (field.fCustomDataAttributeDataType) {
    case 'STRING':
    case 'DATE':
    case 'NUMBER':
    case 'TEXT':
      return _.get(data, field.fCustomDataAttributeName);

    case 'BOOLEAN':
      return _.get(data, field.fCustomDataAttributeName) === 'true'
        ? t('laTrue')
        : t('laFalse');

    case 'ENUM':
      return jsSelectName(
        jsonEnumALL[
          '__CUSTOMDATA__' + key + '__' + field.fCustomDataAttributeName
        ],
        _.get(data, field.fCustomDataAttributeName) as string,
      );

    case 'MULTISELECT':
      return ((_.get(data, field.fCustomDataAttributeName) as string[]) || [])
        .map((item) => {
          return jsSelectName(
            jsonEnumALL[
              '__CUSTOMDATA__' + key + '__' + field.fCustomDataAttributeName
            ],
            item,
          );
        })
        .join(', ');

    default:
      return _.get(data, field.fCustomDataAttributeName);
  }
};

const toPascalCase = (str: string) => {
  return str
    .replace(new RegExp('[-_]+', 'g'), ' ')
    .replace(new RegExp('[^\\w\\s]', 'g'), '')
    .replace(new RegExp('\\s+(.)(\\w+)', 'g'), ($1, $2, $3) => {
      return $2.toUpperCase() + $3.toLowerCase();
    })
    .replace(new RegExp('\\s', 'g'), '')
    .replace(new RegExp('\\w'), (s) => s.toUpperCase());
};
